import React from 'react'
import { normalizeResolvedVideoCallQuality } from '../../../routing/videoCallQualityContract'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function pickDefined(source, keys) {
  return keys.reduce((result, key) => {
    if (source[key] !== undefined) {
      result[key] = source[key]
    }

    return result
  }, {})
}

function normalizeBoolean(value, fallbackValue) {
  return typeof value === 'boolean' ? value : fallbackValue
}

function normalizeRole(role) {
  return role === 'audience' ? 'audience' : 'host'
}

function mergePlainObject(baseValue, nextValue) {
  if (!isPlainObject(nextValue)) {
    return baseValue
  }

  return {
    ...baseValue,
    ...nextValue,
  }
}

export const AGORA_RTC_LAYOUTS = Object.freeze({
  grid: 'grid',
  pin: 'pin',
})

export const AGORA_TOGGLE_STATES = Object.freeze({
  disabled: 0,
  enabled: 1,
  disabling: 2,
  enabling: 3,
})

export const AGORA_RTC_ADAPTER_CAPABILITIES = Object.freeze({
  screenshare: true,
  remoteMute: true,
  auditObserverJoin: true,
  playbackDeviceSelection: true,
  deviceHotSwap: true,
  customVideoPlaceholder: true,
  usernameOverlay: true,
  rtmBackedPresence: true,
})

const TOGGLE_STATE_VALUES = Object.values(AGORA_TOGGLE_STATES)

export function createAgoraRtcAdapterCapabilities(overrides = {}) {
  return {
    ...AGORA_RTC_ADAPTER_CAPABILITIES,
    ...(isPlainObject(overrides) ? overrides : {}),
  }
}

export function normalizeAgoraRtcLayout(layout, fallbackValue = AGORA_RTC_LAYOUTS.pin) {
  if (layout === 0 || layout === '0') {
    return AGORA_RTC_LAYOUTS.grid
  }

  if (layout === 1 || layout === '1') {
    return AGORA_RTC_LAYOUTS.pin
  }

  if (typeof layout !== 'string') {
    return fallbackValue
  }

  const normalizedLayout = layout.trim().toLowerCase()

  if (normalizedLayout === 'grid') {
    return AGORA_RTC_LAYOUTS.grid
  }

  if (normalizedLayout === 'pin' || normalizedLayout === 'pinned') {
    return AGORA_RTC_LAYOUTS.pin
  }

  return fallbackValue
}

export function normalizeAgoraToggleState(
  toggleState,
  fallbackValue = AGORA_TOGGLE_STATES.disabled,
) {
  return TOGGLE_STATE_VALUES.includes(toggleState) ? toggleState : fallbackValue
}

export function createAgoraRtcCompatibilityProps({
  rtcProps = {},
  rtmProps = null,
  styleProps = {},
  callbacks = {},
  rtmCallbacks = {},
  renderless = false,
} = {}) {
  const safeRtcProps = isPlainObject(rtcProps) ? rtcProps : {}
  const safeRtmProps = isPlainObject(rtmProps) ? rtmProps : null

  return {
    rtcProps: {
      ...pickDefined(safeRtcProps, [
        'appId',
        'channel',
        'uid',
        'token',
        'tokenUrl',
        'screenshareUid',
        'screenshareToken',
        'customRtcClient',
        'dualStreamMode',
        'cameraDeviceId',
        'microphoneDeviceId',
        'playbackDeviceId',
        'CustomVideoPlaceholder',
      ]),
      activeSpeaker: normalizeBoolean(safeRtcProps.activeSpeaker, false),
      callActive: normalizeBoolean(safeRtcProps.callActive, true),
      enableDualStream: normalizeBoolean(safeRtcProps.enableDualStream, false),
      enableScreensharing: normalizeBoolean(safeRtcProps.enableScreensharing, false),
      enableAudio: normalizeBoolean(safeRtcProps.enableAudio, true),
      enableVideo: normalizeBoolean(safeRtcProps.enableVideo, true),
      disableRtm: normalizeBoolean(safeRtcProps.disableRtm, !safeRtmProps),
      layout: normalizeAgoraRtcLayout(safeRtcProps.layout),
      role: normalizeRole(safeRtcProps.role),
      videoQuality: normalizeResolvedVideoCallQuality(safeRtcProps.videoQuality),
    },
    rtmProps: safeRtmProps
      ? {
          ...pickDefined(safeRtmProps, ['username', 'token', 'uid']),
          displayUsername: normalizeBoolean(safeRtmProps.displayUsername, false),
          showPopUpBeforeRemoteMute: normalizeBoolean(
            safeRtmProps.showPopUpBeforeRemoteMute,
            true,
          ),
          isThisUserAllowedToMuteOthers: normalizeBoolean(
            safeRtmProps.isThisUserAllowedToMuteOthers,
            true,
          ),
        }
      : undefined,
    styleProps: isPlainObject(styleProps) ? styleProps : {},
    callbacks: isPlainObject(callbacks) ? callbacks : {},
    rtmCallbacks: isPlainObject(rtmCallbacks) ? rtmCallbacks : {},
    renderless: normalizeBoolean(renderless, false),
  }
}

export function mergeAgoraRtcCompatibilityProps(baseProps = {}, nextProps = {}) {
  return createAgoraRtcCompatibilityProps({
    ...baseProps,
    ...nextProps,
    rtcProps: mergePlainObject(baseProps?.rtcProps || {}, nextProps?.rtcProps),
    rtmProps:
      nextProps?.rtmProps === undefined
        ? baseProps?.rtmProps
        : isPlainObject(nextProps?.rtmProps)
          ? nextProps.rtmProps
          : null,
    styleProps: mergePlainObject(baseProps?.styleProps || {}, nextProps?.styleProps),
    callbacks: mergePlainObject(baseProps?.callbacks || {}, nextProps?.callbacks),
    rtmCallbacks: mergePlainObject(baseProps?.rtmCallbacks || {}, nextProps?.rtmCallbacks),
  })
}

let agoraRtcUIKitModulePromise = null

export function loadAgoraRtcUIKitModule() {
  if (!agoraRtcUIKitModulePromise) {
    agoraRtcUIKitModulePromise = import('./AgoraRtcSurface').then((module) => ({
      AgoraRtcSurface: module.default,
    }))
  }

  return agoraRtcUIKitModulePromise
}

export function preloadAgoraRtcUIKitModule() {
  return loadAgoraRtcUIKitModule().then(() => undefined)
}

const LazyAgoraRtcUIKit = React.lazy(async () => {
  const module = await loadAgoraRtcUIKitModule()

  return {
    default: module.AgoraRtcSurface,
  }
})

export function AgoraRtcCompatibilitySurface({
  fallback = null,
  children = null,
  renderless = false,
  rtcProps,
  rtmProps,
  styleProps,
  callbacks,
  rtmCallbacks,
}) {
  const compatibilityProps = React.useMemo(
    () =>
      createAgoraRtcCompatibilityProps({
        rtcProps,
        rtmProps,
        styleProps,
        callbacks,
        rtmCallbacks,
        renderless,
      }),
    [callbacks, renderless, rtcProps, rtmCallbacks, rtmProps, styleProps],
  )

  return React.createElement(
    React.Suspense,
    { fallback },
    React.createElement(
      LazyAgoraRtcUIKit,
      {
        ...compatibilityProps,
        renderless,
      },
      children,
    ),
  )
}

AgoraRtcCompatibilitySurface.displayName = 'AgoraRtcCompatibilitySurface'
